import CryptoJS from 'crypto-js';
import { shouldLog, LogCategory, getLogLevel } from '@/config/logging';
import { hmacLogger } from './communication/logging/hmacLogger';

/**
 * Gera UUID v4 SEM HÍFENS usando crypto.randomUUID() (navegador moderno)
 * Fallback para geração manual se não disponível
 * 
 * ⚠️ IMPORTANTE: O backend espera UUID SEM hífens conforme PADRAO_VALIDACAO_HMAC.md
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    // ✅ CORREÇÃO: Remover hífens conforme especificação
    return crypto.randomUUID().replace(/-/g, '');
  }
  
  // Fallback para navegadores antigos (já sem hífens)
  return 'xxxxxxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Canonicaliza um valor para o formato esperado pelo backend C#
 * 
 * IMPORTANTE: O backend usa um formato CUSTOMIZADO, não JSON.stringify() padrão:
 * - SEM espaços após os dois pontos
 * - Chaves ordenadas alfabeticamente
 * - Formato: {key:"value"} ao invés de { "key": "value" }
 * 
 * ⚠️ REGRAS CRÍTICAS (PADRAO_VALIDACAO_HMAC.md):
 * - null (valor JSON): serializa como string "null" (com aspas)
 * - undefined: serializa como string "undefined" (não deve ocorrer em JSON válido)
 * - Strings: com aspas e escape correto (JSON.stringify)
 * - Números: sem aspas
 * - Booleanos: sem aspas (true/false lowercase)
 * 
 * @param value Valor a ser canonicalizado
 * @returns String canonicalizada
 */
function canonicalizeValue(value: any): string {
  // 1. Array → [item1,item2,...]
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]';
    }
    const items = value.map(item => canonicalizeValue(item));
    return `[${items.join(',')}]`;
  }
  
  // 2. Date → "2025-11-21T04:27:16.700Z" (ISO 8601 explícito)
  // ⚠️ IMPORTANTE: Verificar Date ANTES de object (Date instanceof Object = true)
  // Backend C# MessageHandlerService.CanonicalizeToken trata Date como JTokenType.Date
  // Formato: "yyyy-MM-ddTHH:mm:ss.fffK" (ISO 8601 com 3 dígitos de milissegundos)
  if (value instanceof Date) {
    const year = value.getUTCFullYear();
    const month = String(value.getUTCMonth() + 1).padStart(2, '0');
    const day = String(value.getUTCDate()).padStart(2, '0');
    const hours = String(value.getUTCHours()).padStart(2, '0');
    const minutes = String(value.getUTCMinutes()).padStart(2, '0');
    const seconds = String(value.getUTCSeconds()).padStart(2, '0');
    const milliseconds = String(value.getUTCMilliseconds()).padStart(3, '0');
    const isoString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
    // Backend usa JsonConvert.SerializeObject → adiciona aspas e escapa
    return JSON.stringify(isoString);
  }
  
  // 3. Objeto (mas não null, não Date, não Array)
  if (typeof value === 'object' && value !== null) {
    return canonicalizeBody(value);
  }
  
  // 4. Null/undefined → "null" (string, não string vazia!)
  // Backend C# MessageHandlerService.CanonicalizeToken trata null como JTokenType.Null
  // e retorna a string "null" (sem aspas), não string vazia!
  // 
  // IMPORTANTE: O formato canonicalizado usa "null" (sem aspas) para valores null:
  // {layer:null,page:null,tipo:null} ✅
  // {layer:,page:,tipo:} ❌ (string vazia - ERRADO!)
  if (value === null || value === undefined) {
    return 'null';  // ← String "null" (sem aspas), conforme backend C#
  }
  
  // 5. String → "valor" com aspas e escape
  if (typeof value === 'string') {
    // Backend C#: JsonConvert.SerializeObject(strValue)
    // Adiciona aspas e escapa caracteres especiais (\, ", newline, etc.)
    return JSON.stringify(value);
  }
  
  // 6. Number → 123 ou 45.67 (sem aspas)
  if (typeof value === 'number') {
    // Backend C#: Convert.ToDouble().ToString(CultureInfo.InvariantCulture)
    // .NET Framework produz no máximo 15 dígitos significativos (R format).
    // JS Number.toString() usa "shortest representation" que pode divergir.
    // Solução: usar toPrecision(15) para alinhar com C#, removendo trailing zeros.
    if (Number.isInteger(value)) {
      return value.toString();
    }
    // Decimais: alinhar com C# ToString(CultureInfo.InvariantCulture)
    // toPrecision(15) → "90.0000024754701" (igual ao C#)
    // Remover trailing zeros: "270.000000550917" não "270.0000005509170"
    const formatted = value.toPrecision(15);
    // Remover trailing zeros após ponto decimal, mas manter pelo menos 1 dígito
    if (formatted.includes('.')) {
      return formatted.replace(/0+$/, '').replace(/\.$/, '');
    }
    return formatted;
  }
  
  // 7. Boolean → true ou false (lowercase, sem aspas)
  if (typeof value === 'boolean') {
    // Backend C#: boolValue.ToString().ToLowerInvariant()
    return value ? 'true' : 'false';
  }
  
  // 8. Default → serializar como string
  // Fallback para tipos não reconhecidos
  return JSON.stringify(String(value));
}

/**
 * Canonicaliza um objeto no formato esperado pelo backend C#
 * 
 * REGRAS DO BACKEND C#:
 * 1. Chaves ordenadas ALFABETICAMENTE (StringComparer.Ordinal)
 * 2. SEM ESPAÇOS após os dois pontos: {key:"value"}
 * 3. Formato: {chave1:valor1,chave2:valor2}
 * 
 * Exemplo:
 * Input:  { filename: "layout.pdf", options: { quality: "high" } }
 * Output: {filename:"layout.pdf",options:{quality:"high"}}
 * 
 * @param obj Objeto a ser canonicalizado
 * @returns String no formato {key:value,key:value}
 */
function canonicalizeBody(obj: any): string {
  if (obj === null || obj === undefined) {
    return '{}';
  }
  
  if (typeof obj !== 'object' || Array.isArray(obj)) {
    return JSON.stringify(obj);
  }
  
  // Filtrar keys com valor undefined (JSON.stringify as remove, C# nunca as recebe)
  // Manter keys com valor null (JSON.stringify as preserva como null)
  const sortedKeys = Object.keys(obj)
    .filter(key => obj[key] !== undefined)
    .sort();

  // Construir no formato: key:value,key:value
  const parts = sortedKeys.map(key => {
    const value = canonicalizeValue(obj[key]);
    return `${key}:${value}`;
  });
  
  return `{${parts.join(',')}}`;
}

/**
 * Gera assinatura HMAC-SHA256 conforme especificação do backend
 * 
 * ⚠️ ESPECIFICAÇÃO COMPLETA (PADRAO_VALIDACAO_HMAC.md):
 * 
 * **Algoritmo:** HMAC-SHA256
 * **Formato do payload:** `${nonce}|${timestamp}|${action}|${canonicalBody}`
 * **Separador:** `|` (pipe)
 * **Nonce:** UUID v4 (ex: "550e8400-e29b-41d4-a716-446655440000")
 * **Timestamp:** Unix timestamp em SEGUNDOS (não milissegundos!) ⚠️
 * **CanonicalBody:** Formato customizado {key:value} SEM aspas nas chaves
 * **Output:** Base64 (não hexadecimal!)
 * **Encoding:** UTF-8
 * 
 * ⚠️ CHECKLIST CRÍTICO:
 * 1. ✅ Timestamp em SEGUNDOS: Math.floor(Date.now() / 1000)
 * 2. ✅ Ordem do payload: nonce|timestamp|action|canonicalBody
 * 3. ✅ Chave HMAC decodificada: CryptoJS.enc.Base64.parse(hmacSecret)
 * 4. ✅ Canonicalização customizada: {key:value} sem aspas nas chaves
 * 
 * Posição na mensagem final:
 * ```
 * {
 *   action: "save-layout",
 *   requestId: "uuid-v4",
 *   sessionToken: "abc123",      // ← NA RAIZ!
 *   data: {
 *     auth: {                     // ← DENTRO de data.auth!
 *       signature: "hmac-base64", // ← Base64!
 *       nonce: "uuid-v4",
 *       timestamp: 1731877494     // ← SEGUNDOS!
 *     },
 *     filename: "layout.pdf"
 *   }
 * }
 * ```
 * 
 * @param action Nome da ação (ex: "save-layout")
 * @param businessData Dados de negócio (SEM o campo auth)
 * @param hmacSecret Chave secreta Base64 de window.sessionCrypto.hmacKeyB64
 * @returns Objeto { signature, nonce, timestamp } para incluir em data.auth
 */
export function generateAuth(
  action: string,
  businessData: any,
  hmacSecret: string
): { signature: string; nonce: string; timestamp: number } {
  // 1. ✅ Gerar nonce como UUID v4 (PADRAO_VALIDACAO_HMAC.md linha 112-130)
  const nonce = generateUUID();
  
  // 2. ✅ Gerar timestamp em SEGUNDOS não milissegundos! (linha 142-165)
  const timestamp = Math.floor(Date.now() / 1000);
  
  // 3. ✅ Preparar canonicalBody com formato CUSTOMIZADO (linha 172-228)
  // IMPORTANTE: Formato {key:value} SEM aspas nas chaves (não é JSON.stringify!)
  // Backend espera: {bootstrap:{accessToken:"...",isAdmin:false},url:"http://..."}
  // ✅ CORREÇÃO: Sempre chamar canonicalizeBody conforme PADRAO_VALIDACAO_HMAC.md
  // Objetos vazios {} devem retornar "{}" (não string vazia)
  
  // 🔍 DEBUG: Verificar idempotencyKey antes de canonicalizar
  const hasIdempotencyKey = businessData && 'idempotencyKey' in businessData;
  const idempotencyKeyValue = hasIdempotencyKey ? (businessData as any).idempotencyKey : undefined;
  const allKeys = businessData ? Object.keys(businessData).sort() : [];
  
  const canonicalBody = canonicalizeBody(businessData || {});
  
  // 🔍 DEBUG: Verificar se idempotencyKey aparece no canonicalBody
  const idempotencyKeyInCanonical = hasIdempotencyKey && canonicalBody.includes('idempotencyKey');
  const idempotencyKeyPosition = idempotencyKeyInCanonical ? canonicalBody.indexOf('idempotencyKey') : -1;
  
  // 4. ✅ Construir payload com ORDEM EXATA: nonce|timestamp|action|canonicalBody (linha 324-334)
  const payload = `${nonce}|${timestamp}|${action}|${canonicalBody}`;
  
  // ✅ CORREÇÃO CRÍTICA: Converter payload para UTF-8 explicitamente
  // Backend C# usa Encoding.UTF8.GetBytes(), então precisamos garantir UTF-8
  // CryptoJS por padrão pode usar UTF-16 (padrão JavaScript)
  // Caracteres multi-byte (á, Ê, etc.) devem ser tratados como UTF-8 (2 bytes cada)
  const payloadUtf8 = CryptoJS.enc.Utf8.parse(payload);
  
  // 5. ✅ Gerar HMAC-SHA256 com chave DECODIFICADA de Base64 e payload em UTF-8
  const hmac = CryptoJS.HmacSHA256(payloadUtf8, CryptoJS.enc.Base64.parse(hmacSecret));
  
  // 6. ✅ Retornar como Base64 (linha 356)
  const signature = CryptoJS.enc.Base64.stringify(hmac);
  
  // ✅ LOGS DE DEBUG: Conforme solicitação do backend para comparação
  // Ativos apenas em desenvolvimento ou quando DEBUG_HMAC estiver habilitado
  const DEBUG_HMAC = import.meta.env.DEV || (typeof window !== 'undefined' && (window as any).DEBUG_HMAC);
  
  if (DEBUG_HMAC) {
    console.log('Frontend Payload:', `${nonce}|${timestamp}|${action}|${canonicalBody}`);
    console.log('Frontend CanonicalBody:', canonicalBody);
    console.log('Frontend Signature:', signature);
    
    // Hex dumps (apenas quando necessário para diagnóstico byte-a-byte)
    if (typeof window !== 'undefined' && (window as any).DEBUG_HMAC_HEX) {
      try {
        const payloadBytes = new TextEncoder().encode(payload);
        const payloadHex = Array.from(payloadBytes)
          .map(b => b.toString(16).padStart(2, '0').toUpperCase())
          .join(' ');
        console.log('Frontend Payload Hex:', payloadHex);
        
        const canonicalBytes = new TextEncoder().encode(canonicalBody);
        const canonicalHex = Array.from(canonicalBytes)
          .map(b => b.toString(16).padStart(2, '0').toUpperCase())
          .join(' ');
        console.log('Frontend CanonicalBody Hex:', canonicalHex);
      } catch (e) {
        // Ignorar erros de encoding
      }
    }
  }
  
  // 🔍 LOG HMAC: Registrar geração de HMAC
  const hmacSecretAvailable = !!hmacSecret;
  hmacLogger.logHmacGeneration(
    action,
    businessData,
    canonicalBody,
    signature,
    nonce,
    timestamp,
    hmacSecretAvailable
  );
  
  // 🔍 DEBUG ESPECIAL: Log detalhado para salvararquivo e nesting actions
  if ((action === 'salvararquivo' || action === 'nesting:apply-native' || action === 'nesting:collect-native') && typeof window !== 'undefined' && (window as any).sendToTerminal) {
    try {
      // Converter payload para hex para comparação byte-a-byte com backend
      const payloadBytes = [];
      for (let i = 0; i < payload.length; i++) {
        const charCode = payload.charCodeAt(i);
        if (charCode < 0x80) {
          // ASCII (1 byte)
          payloadBytes.push(charCode.toString(16).padStart(2, '0'));
        } else if (charCode < 0x800) {
          // 2 bytes UTF-8
          payloadBytes.push(((charCode >> 6) | 0xC0).toString(16).padStart(2, '0'));
          payloadBytes.push(((charCode & 0x3F) | 0x80).toString(16).padStart(2, '0'));
        } else if (charCode < 0x10000) {
          // 3 bytes UTF-8
          payloadBytes.push(((charCode >> 12) | 0xE0).toString(16).padStart(2, '0'));
          payloadBytes.push((((charCode >> 6) & 0x3F) | 0x80).toString(16).padStart(2, '0'));
          payloadBytes.push(((charCode & 0x3F) | 0x80).toString(16).padStart(2, '0'));
        }
      }
      const payloadHex = payloadBytes.join('');
      
      // Converter canonical body para hex também
      const canonicalBodyBytes = [];
      for (let i = 0; i < canonicalBody.length; i++) {
        const charCode = canonicalBody.charCodeAt(i);
        if (charCode < 0x80) {
          canonicalBodyBytes.push(charCode.toString(16).padStart(2, '0'));
        } else if (charCode < 0x800) {
          canonicalBodyBytes.push(((charCode >> 6) | 0xC0).toString(16).padStart(2, '0'));
          canonicalBodyBytes.push(((charCode & 0x3F) | 0x80).toString(16).padStart(2, '0'));
        } else if (charCode < 0x10000) {
          canonicalBodyBytes.push(((charCode >> 12) | 0xE0).toString(16).padStart(2, '0'));
          canonicalBodyBytes.push((((charCode >> 6) & 0x3F) | 0x80).toString(16).padStart(2, '0'));
          canonicalBodyBytes.push(((charCode & 0x3F) | 0x80).toString(16).padStart(2, '0'));
        }
      }
      const canonicalBodyHex = canonicalBodyBytes.join('');
      
      (window as any).sendToTerminal(`🔐 [HMAC:${action.toUpperCase()}_DEBUG]`, {
        action,
        nonce,
        nonceLength: nonce.length,
        timestamp,
        timestampDigits: String(timestamp).length,
        
        // Payload completo
        payloadLength: payload.length,
        payloadPreview: payload.substring(0, 200) + (payload.length > 200 ? '...' : ''),
        payloadFull: payload, // Payload completo para comparação
        
        // Canonical body
        canonicalBodyLength: canonicalBody.length,
        canonicalBodyPreview: canonicalBody.substring(0, 300) + (canonicalBody.length > 300 ? '...' : ''),
        canonicalBodyFull: canonicalBody, // Canonical body completo
        
        // Hex dump do payload para comparação byte-a-byte com backend
        payloadHexLength: payloadHex.length,
        payloadHexPreview: payloadHex.substring(0, 400) + (payloadHex.length > 400 ? '...' : ''),
        payloadHexFull: payloadHex, // Hex completo do payload
        
        // ✅ CORREÇÃO: Hex dump do CANONICAL BODY (para comparar com backend)
        canonicalBodyHexLength: canonicalBodyHex.length,
        canonicalBodyHexPreview: canonicalBodyHex.substring(0, 400) + (canonicalBodyHex.length > 400 ? '...' : ''),
        canonicalBodyHexFull: canonicalBodyHex, // Hex completo do canonical body
        
        // Signature
        signature,
        signatureLength: signature.length,
        
        // UTF-8 info
        payloadUtf8Info: 'CryptoJS.enc.Utf8.parse() usado ✅',
        
        // ✅ CORREÇÃO: Busca por caracteres multi-byte NO CANONICAL BODY (não no payload)
        multiByteChars: (() => {
          const chars: Array<{ char: string; code: string; index: number }> = [];
          for (let i = 0; i < canonicalBody.length; i++) {
            const code = canonicalBody.charCodeAt(i);
            if (code > 127) {
              chars.push({
                char: canonicalBody[i],
                code: `U+${code.toString(16).toUpperCase().padStart(4, '0')}`,
                index: i // Índice relativo ao canonicalBody, não ao payload
              });
            }
          }
          return chars;
        })()
      });
    } catch (e) {
      // Erro ao logar - silencioso
    }
  }
  
  // 🔍 DEBUG: Log detalhado para validação conforme PADRAO_VALIDACAO_HMAC.md
  // Apenas no nível DEBUG (3)
  if (typeof window !== 'undefined' && (window as any).sendToTerminal) {
    try {
      if (shouldLog(LogCategory.AUTH, getLogLevel())) {
        (window as any).sendToTerminal('🔐 [HMAC:GENERATED]', {
        action,
        
        // Validação checklist item 1: Timestamp em SEGUNDOS ✅
        timestamp,
        timestampFormat: 'Unix seconds (Math.floor(Date.now() / 1000)) ✅',
        timestampDigits: String(timestamp).length, // Deve ser ~10 dígitos
        timestampValidation: String(timestamp).length === 10 ? '✅ CORRETO' : '❌ ERRADO',
        
        // Validação checklist item 2: Ordem do payload ✅
        payloadOrder: 'nonce|timestamp|action|canonicalBody ✅',
        payloadPreview: `${nonce.substring(0, 8)}${nonce.length > 8 ? '...' : ''}|${timestamp}|${action}|${canonicalBody.substring(0, 30)}${canonicalBody.length > 30 ? '...' : ''}`,
        
        // Validação checklist item 3: Chave HMAC decodificada ✅
        hmacKeyDecoded: 'CryptoJS.enc.Base64.parse(hmacSecret) ✅',
        
        // Validação checklist item 4: Canonicalização customizada ✅
        canonicalBodyFormat: '{key:value} SEM aspas nas chaves ✅',
        canonicalBodyLength: canonicalBody.length,
        canonicalBodyPreview: canonicalBody.substring(0, 150) + (canonicalBody.length > 150 ? '...' : ''),
        
        // Validações adicionais
        nonce,
        nonceFormat: 'UUID v4 ✅',
        nonceLength: nonce.length,
        signatureLength: signature.length,
        signaturePreview: signature.substring(0, 20) + '...',
        signatureFormat: 'Base64 ✅',
        dataKeys: allKeys,
        
        // 🔍 DEBUG: Rastreamento detalhado de idempotencyKey
        idempotencyKeyDebug: {
          presentInBusinessData: hasIdempotencyKey,
          value: hasIdempotencyKey ? (typeof idempotencyKeyValue === 'string' ? idempotencyKeyValue.substring(0, 50) + (idempotencyKeyValue.length > 50 ? '...' : '') : String(idempotencyKeyValue)) : undefined,
          type: hasIdempotencyKey ? typeof idempotencyKeyValue : undefined,
          includedInCanonical: idempotencyKeyInCanonical,
          positionInCanonical: idempotencyKeyPosition >= 0 ? idempotencyKeyPosition : undefined,
          canonicalSnippet: idempotencyKeyInCanonical && idempotencyKeyPosition >= 0 
            ? canonicalBody.substring(Math.max(0, idempotencyKeyPosition - 20), Math.min(canonicalBody.length, idempotencyKeyPosition + 80))
            : undefined,
          status: hasIdempotencyKey 
            ? (idempotencyKeyInCanonical ? '✅ INCLUÍDO na canonicalização' : '❌ NÃO encontrado na canonicalização')
            : '⚠️ NÃO presente em businessData'
        },
        
        // Referência à documentação
        spec: 'PADRAO_VALIDACAO_HMAC.md ✅',
        checklistValidation: '1.✅ 2.✅ 3.✅ 4.✅ - TODOS CORRETOS'
        });
      }
    } catch (e) {
      // Erro ao logar HMAC - silencioso
    }
  }
  
  return {
    signature,
    nonce,
    timestamp
  };
}



